import React, { useMemo, useState } from 'react';
import { Table, Card, Typography, Space, Tooltip, Button, Popconfirm, Tag, Badge, List, Spin, Empty, Collapse, Divider, Upload, Col, Row, message } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  CopyOutlined, 
  NodeIndexOutlined, 
  RocketOutlined, 
  DownOutlined, 
  RightOutlined,
  RobotOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  AuditOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  SyncOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SkinOutlined,
  CheckCircleOutlined,
  EditOutlined
} from '@ant-design/icons';
import { 
  SourceProcess, 
  SourceChapter, 
  SourceCharacter, 
  SourceScene, 
  SourcePanel
} from '../../../types';
import { formatDate } from '../../../utils';
import SceneInlineEditor from './SceneInlineEditor';
import PanelCaptionEditor from './PanelCaptionEditor';

const { Title, Text } = Typography;

interface ProcessPanelRowsProps {
  isMobile: boolean;
  chapters: SourceChapter[];
  processesLoading: boolean;
  processesData: { data: SourceProcess[] } | null;
  expandedRowKeys: string[];
  handleRowExpand: (expanded: boolean, record: any) => void;
  handleCreate: () => void;
  canCreateProcess: boolean;
  latestProcess: SourceProcess | null;
  scenesData: Record<string, SourceScene[]>;
  scenesLoading: Record<string, boolean>;
  storyCharacters: SourceCharacter[];
  asyncLoading: boolean;
  selectedProcess: SourceProcess | null;
  currentExtractingProcessId: string | null;
  currentExtractingSceneId: string | null;
  activeSceneKeys: Record<string, string | string[]>;
  setActiveSceneKeys: React.Dispatch<React.SetStateAction<Record<string, string | string[]>>>;
  handleSceneExpand: (sceneId: string) => void;
  panelsData: Record<string, SourcePanel[]>;
  panelsLoading: Record<string, boolean>;
  activeDetailKeys: Record<string, string | string[]>;
  setActiveDetailKeys: React.Dispatch<React.SetStateAction<Record<string, string | string[]>>>;
  getCharacterName: (id: string) => string;
  getSceneDoneCount: (sceneId: string) => number;
  handleExtractCharacters: (process: SourceProcess) => void;
  handleManageCharacterCheckpoints: (character: SourceCharacter) => void;
  handleExtractScenes: (process: SourceProcess) => void;
  handleClearScenes: (process: SourceProcess) => void;
  handleDraftPanels: (scene: SourceScene) => void;
  handlePlanVisuals: (scene: SourceScene) => void;
  setSelectedSceneForBulk: (scene: SourceScene) => void;
  setIsBulkModalVisible: (visible: boolean) => void;
  handleReviewScene: (scene: SourceScene) => void;
  handleReExtractPanels: (scene: SourceScene) => void;
  handleClearPanels: (scene: SourceScene) => void;
  handleManagePanelImage: (panel: SourcePanel) => void;
  handleUploadPanelImage: (panel: SourcePanel, file: File) => void;
  handleAiRefinePanel: (panel: SourcePanel) => void;
  handleDeletePanel: (panel: SourcePanel) => void;
  handleCloneShortProcess: (process: SourceProcess) => void;
  handleViewDetail: (process: SourceProcess) => void;
  handleDelete: (process: SourceProcess) => void;
  setAutoCloneProcess: (process: SourceProcess) => void;
  setIsAutoCloneModalVisible: (visible: boolean) => void;
  setAutoEpisodeProcess: (process: SourceProcess) => void;
  setIsAutoEpisodeModalVisible: (visible: boolean) => void;
  getStatusTag: (status: string) => React.ReactNode;
  getProcessCompletionStats: (processId: string) => { isComplete: boolean; completedCount: number; totalCount: number };
  colorBorder: string;
  handleRefresh?: () => void;
  handleOpenSceneOutfits: (scene: SourceScene) => void;
  onSceneUpdated: (processId: string, updatedScene: SourceScene) => void;
  onPanelUpdated: (sceneId: string, updatedPanel: SourcePanel) => void;
}

const countWords = (text?: string) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};

const ProcessPanelRows: React.FC<ProcessPanelRowsProps> = (props) => {
  const {
    isMobile,
    chapters,
    processesLoading,
    processesData,
    expandedRowKeys,
    handleRowExpand,
    handleCreate,
    canCreateProcess,
    latestProcess,
    scenesData,
    scenesLoading,
    storyCharacters,
    asyncLoading,
    selectedProcess,
    currentExtractingProcessId,
    currentExtractingSceneId,
    activeSceneKeys,
    setActiveSceneKeys,
    handleSceneExpand,
    panelsData,
    panelsLoading,
    activeDetailKeys,
    setActiveDetailKeys,
    getCharacterName,
    getSceneDoneCount,
    handleExtractCharacters,
    handleManageCharacterCheckpoints,
    handleExtractScenes,
    handleClearScenes,
    handleDraftPanels,
    handlePlanVisuals,
    setSelectedSceneForBulk,
    setIsBulkModalVisible,
    handleReviewScene,
    handleReExtractPanels,
    handleClearPanels,
    handleManagePanelImage,
    handleUploadPanelImage,
    handleAiRefinePanel,
    handleDeletePanel,
    handleCloneShortProcess,
    handleViewDetail,
    handleDelete,
    setAutoCloneProcess,
    setIsAutoCloneModalVisible,
    setAutoEpisodeProcess,
    setIsAutoEpisodeModalVisible,
    getStatusTag,
    getProcessCompletionStats,
    colorBorder,
    handleRefresh,
    handleOpenSceneOutfits,
    onSceneUpdated,
    onPanelUpdated,
  } = props;

  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [editingPanelId, setEditingPanelId] = useState<string | null>(null);

  const treeData = useMemo(() => {
    if (!processesData?.data) return [];
    
    const groups = new Map<string, SourceProcess[]>();
    const rootNodes: any[] = [];

    processesData.data.forEach(process => {
      const startChapterId = process.chapterIds[0] || 'unknown';
      if (!groups.has(startChapterId)) {
        groups.set(startChapterId, []);
      }
      groups.get(startChapterId)!.push(process);
    });

    Array.from(groups.entries()).forEach(([startChapterId, procs]) => {
      procs.sort((a, b) => {
        if (a.orderIndex === b.orderIndex) {
            if (a.videoForm === 'LONG' && b.videoForm !== 'LONG') return -1;
            if (a.videoForm !== 'LONG' && b.videoForm === 'LONG') return 1;
        }
        return b.orderIndex - a.orderIndex;
      });

      const startChapter = chapters.find(c => c.id === startChapterId);
      const groupName = startChapter ? `Từ ${startChapter.name || `Chapter ${chapters.findIndex(c => c.id === startChapter.id) + 1}`}` : 'Chapter không xác định';

      rootNodes.push({
        id: `group-${startChapterId}`,
        isGroup: true,
        name: groupName,
        chapterIds: procs[0]?.chapterIds || [],
        createdAt: procs[0]?.createdAt || new Date().toISOString(),
        children: procs,
      });
    });

    return rootNodes.sort((a, b) => {
      const aIndex = a.children?.[0]?.orderIndex || 0;
      const bIndex = b.children?.[0]?.orderIndex || 0;
      return bIndex - aIndex; // Descending
    });
  }, [processesData, chapters]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => {
        if (record.isGroup) {
          return (
            <Space>
              <FolderOpenOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <Text strong style={{ fontSize: '15px' }}>{name}</Text>
            </Space>
          );
        }
        return (
          <Space style={{ paddingLeft: record.parentProcessId ? 30 : 0 }}>
            <FileTextOutlined style={{ color: record.parentProcessId ? '#faad14' : '#8c8c8c' }} />
            <Space direction="vertical" size={0}>
              <Text strong style={{ color: '#262626' }}>{name}</Text>
              <Space>
                {record.videoForm && (
                  <Tag color={record.videoForm === 'LONG' ? 'cyan' : 'purple'} style={{ fontSize: '10px' }}>
                    {record.videoForm === 'LONG' ? '16:9' : '9:16'}
                  </Tag>
                )}
                {record.parentProcessId && (
                  <Tag color="orange" style={{ fontSize: '10px' }} icon={<SyncOutlined />}>
                    SUB
                  </Tag>
                )}
              </Space>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Order',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 100,
      render: (order: number, record: any) => record.isGroup ? null : (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          Process #{order}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => {
        if (record.isGroup) {
          const totalProcs = record.children.length;
          const completedProcs = record.children.filter((p: any) => {
            const stats = getProcessCompletionStats(p.id);
            return stats.isComplete;
          }).length;
          const allCompleted = completedProcs === totalProcs && totalProcs > 0;
          return (
            <Badge count={`${completedProcs}/${totalProcs}`} overflowCount={999} color={allCompleted ? '#52c41a' : '#1890ff'}>
              <Tag color={allCompleted ? 'success' : 'processing'} style={{ marginRight: 0 }}>
                {allCompleted ? 'All Completed' : 'In Progress'}
              </Tag>
            </Badge>
          );
        }

        const { isComplete, completedCount, totalCount } = getProcessCompletionStats(record.id);

        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getStatusTag(status)}
            </div>
            <Tag color={isComplete ? 'success' : 'warning'} style={{ marginTop: 4 }}>
              {completedCount}/{totalCount} panels done
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Chapters',
      dataIndex: 'chapterIds',
      key: 'chapterIds',
      render: (chapterIds: string[], record: any) => record.isGroup ? null : `${chapterIds.length} chapter(s)`,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string, record: any) => record.isGroup ? null : formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_: any, record: any) => {
        if (record.isGroup) return null;

        const { isComplete } = getProcessCompletionStats(record.id);
        return (
          <Space>
            {record.videoForm === 'LONG' && !record.parentProcessId && (
              <Popconfirm
                title="Tạo bản sao Short Video?"
                description="Bản sao sẽ dùng chung nhân vật và checkpoint với bản gốc."
                onConfirm={() => handleCloneShortProcess(record)}
                okText="Đồng ý"
                cancelText="Hủy"
              >
                <Tooltip title="Tạo bản sao Short Video (9:16)">
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    title="Clone to Short"
                  />
                </Tooltip>
              </Popconfirm>
            )}
            {record.videoForm === 'LONG' && !record.parentProcessId && (
              <Tooltip title="Tự động Clone & Process (9:16)">
                <Button
                  type="text"
                  icon={<NodeIndexOutlined />}
                  onClick={() => {
                    setAutoCloneProcess(record);
                    setIsAutoCloneModalVisible(true);
                  }}
                  title="Auto Clone & Process"
                  style={{ color: '#722ed1' }}
                />
              </Tooltip>
            )}
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
              title="View Details"
            />
            <Tooltip title="Tự động tạo Episode & Render Video">
              <Button
                type="text"
                icon={<RocketOutlined />}
                onClick={() => {
                  setAutoEpisodeProcess(record);
                  setIsAutoEpisodeModalVisible(true);
                }}
                title="Auto Episode"
                style={{ color: '#1890ff' }}
                disabled={!isComplete}
              />
            </Tooltip>
            <Popconfirm
              title="Are you sure you want to delete this process?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Delete"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const ProcessCard = ({ process }: { process: SourceProcess }) => {
    const { isComplete, completedCount, totalCount } = getProcessCompletionStats(process.id);
    const chapterNames = process.chapterIds.map(id => {
      const c = chapters.find(ch => ch.id === id);
      return c ? (c.name || `Ch.${chapters.indexOf(c) + 1}`) : id.substring(0, 4);
    }).join(', ');

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderRadius: 8 }}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 15, display: 'block' }}>
              {process.name}
            </Text>
            <Space size={4} wrap style={{ marginTop: 4 }}>
              <Tag color={process.videoForm === 'LONG' ? 'cyan' : 'purple'} style={{ fontSize: '10px' }}>
                {process.videoForm === 'LONG' ? '16:9' : '9:16'}
              </Tag>
              {process.parentProcessId && <Tag color="orange" style={{ fontSize: '10px' }}>SUB</Tag>}
              {getStatusTag(process.status)}
            </Space>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>#{process.orderIndex}</Text>
        </div>

        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)', marginBottom: 8 }}>
          <Text type="secondary">Chapters: </Text>{chapterNames}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Tag color={isComplete ? 'success' : 'warning'} style={{ margin: 0 }}>
            {completedCount}/{totalCount} panels done
          </Tag>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          paddingTop: 8,
          borderTop: `1px solid ${colorBorder}44`
        }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {formatDate(process.createdAt)}
          </Text>
          <Space size={8}>
            {process.videoForm === 'LONG' && !process.parentProcessId && (
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCloneShortProcess(process)}
                style={{ backgroundColor: '#f5f5f5' }}
              />
            )}
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(process)}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Button
              type="text"
              size="small"
              icon={<RocketOutlined />}
              onClick={() => {
                setAutoEpisodeProcess(process);
                setIsAutoEpisodeModalVisible(true);
              }}
              style={{ color: '#1890ff', backgroundColor: '#e6f7ff' }}
              disabled={!isComplete}
            />
            <Popconfirm
              title="Delete this process?"
              onConfirm={() => handleDelete(process)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ backgroundColor: '#fff1f0' }}
              />
            </Popconfirm>
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <Card bodyStyle={isMobile ? { padding: '12px 8px' } : undefined}>
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        marginBottom: 16,
        gap: isMobile ? 12 : 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            Process Management
          </Title>
          <Button 
            type="default" 
            shape="circle"
            icon={<ReloadOutlined spin={processesLoading} />} 
            onClick={handleRefresh}
            title="Refresh Data"
            style={{ marginLeft: 8 }}
          />
        </div>
        <Space>
          <Tooltip title={!canCreateProcess ? `Please approve the latest process (#${latestProcess?.orderIndex}) before creating a new one.` : ""}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              disabled={!canCreateProcess}
              size={isMobile ? 'small' : 'middle'}
            >
              Create Process
            </Button>
          </Tooltip>
        </Space>
      </div>

      {!isMobile ? (
        <Table
          columns={columns}
          dataSource={treeData}
          rowKey="id"
          loading={processesLoading && !processesData}
          defaultExpandAllRows
          rowClassName={(record) => record.isGroup ? 'process-group-row' : 'process-item-row'}
          onRow={(record) => ({
            style: record.isGroup ? {
              backgroundColor: '#fafafa',
              borderLeft: '4px solid #1890ff',
              cursor: 'pointer',
            } : {
              backgroundColor: '#ffffff',
              borderLeft: '4px solid transparent',
            }
          })}
          expandable={{
            expandedRowKeys,
            indentSize: 24,
            onExpand: handleRowExpand,
            expandedRowRender: (record: any) => {
            if (record.isGroup) return null;
            const scenes = scenesData[record.id] || [];
            const sLoading = scenesLoading[record.id];
            const processCharacterIds = record.characterIds || [];
            const processCharacters = storyCharacters.filter(c => processCharacterIds.includes(c.id));
            const charactersWithAvatar = processCharacters.filter(c => !!c.avatarUrl).length;
            const charactersLocalized = processCharacters.filter(c => (c.localizedData as any)?.name).length;
            const allCharactersLocalized = processCharacters.length > 0 && charactersLocalized === processCharacters.length;

            return (
              <div style={{ padding: '16px', background: '#fafafa' }}>
                <Card 
                  size="small" 
                  title={
                    <Space>
                      <span>Characters Management</span>
                      {processCharacterIds.length > 0 && (
                        <>
                          <Tag color={charactersWithAvatar === processCharacterIds.length ? 'success' : 'warning'}>
                            Images: {charactersWithAvatar}/{processCharacterIds.length} done
                          </Tag>
                          <Tag color={allCharactersLocalized ? 'success' : 'warning'}>
                            Localized: {charactersLocalized}/{processCharacterIds.length} done
                          </Tag>
                        </>
                      )}
                    </Space>
                  } 
                  style={{ marginBottom: 16 }}
                  extra={
                    processCharacterIds.length > 0 && (
                      <Button
                        size="small"
                        icon={<RobotOutlined />}
                        onClick={() => handleExtractCharacters(record)}
                        loading={asyncLoading && selectedProcess?.id === record.id}
                      >
                        Re-extract Characters
                      </Button>
                    )
                  }
                >
                  {processCharacterIds.length === 0 ? (
                    <Empty
                      description="No characters associated with this process yet"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={() => handleExtractCharacters(record)}
                        loading={asyncLoading && selectedProcess?.id === record.id}
                      >
                        Extract Characters with AI
                      </Button>
                    </Empty>
                  ) : (
                    <List
                      grid={{ gutter: 16, column: 4 }}
                      dataSource={processCharacters}
                      renderItem={char => (
                        <List.Item>
                          <Card size="small" style={{ borderRadius: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div 
                                style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: '50%', 
                                  overflow: 'hidden', 
                                  background: '#f0f0f0',
                                  border: '1px solid #d9d9d9',
                                  flexShrink: 0,
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleManageCharacterCheckpoints(char)}
                              >
                                {char.avatarUrl ? (
                                  <img 
                                    src={char.avatarUrl} 
                                    alt={char.name} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} 
                                  />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <RobotOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
                                  </div>
                                )}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {char.name}
                                </div>
                                {(char.localizedData as any)?.name && (
                                  <div style={{ fontSize: 12, color: '#52c41a', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Thay thế: {(char.localizedData as any).name}
                                  </div>
                                )}
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                  {char.role}
                                </div>
                              </div>
                              <Button 
                                size="small" 
                                icon={<PictureOutlined />} 
                                onClick={() => handleManageCharacterCheckpoints(char)}
                                type={char.avatarUrl ? "default" : "dashed"}
                                style={!char.avatarUrl ? { color: '#1890ff', borderColor: '#1890ff' } : {}}
                              >
                                {char.avatarUrl ? 'Manage' : 'Create Image'}
                              </Button>
                            </div>
                          </Card>
                        </List.Item>
                      )}
                    />
                  )}
                </Card>

                {processCharacterIds.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    {scenes.length === 0 && sLoading ? (
                      <div style={{ textAlign: 'center', padding: '24px' }}>
                        <Spin tip="Loading scenes..." />
                      </div>
                    ) : (
                      <Spin spinning={sLoading}>
                        {scenes.length === 0 ? (
                          <Empty
                            description="No scenes extracted yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          >
                          <Tooltip title="Tiến hành phân tích và trích xuất danh sách Scenes">
                            <Button
                              type="primary"
                              icon={<RobotOutlined />}
                              onClick={() => handleExtractScenes(record)}
                              loading={currentExtractingProcessId === record.id}
                            >
                              Extract Scenes with AI
                            </Button>
                          </Tooltip>
                          </Empty>
                        ) : (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                              <Title level={5} style={{ margin: 0 }}>
                                Scenes ({scenes.length})
                              </Title>
                              <Space>
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleClearScenes(record)}
                                >
                                  Clear Scenes
                                </Button>
                              </Space>
                            </div>
                            <Collapse 
                              accordion 
                              activeKey={activeSceneKeys[record.id]}
                              onChange={(key) => {
                                setActiveSceneKeys(prev => ({ ...prev, [record.id]: key }));
                                const sceneId = Array.isArray(key) ? key[0] : key;
                                if (sceneId) handleSceneExpand(sceneId as string);
                              }}
                            >
                              {scenes.map((scene, index) => {
                                const panels = panelsData[scene.id] || [];
                                const pLoading = panelsLoading[scene.id];
                                const doneCount = getSceneDoneCount(scene.id);
                                const totalPanels = scene.totalPanels || 0;
                                const isDone = doneCount === totalPanels && totalPanels > 0;
                                
                                const outfits = (scene.outfits as any[]) || [];
                                const totalSceneChars = (scene.characterIds as string[] || []).length;
                                const isOutfitsDone = outfits.length >= totalSceneChars && totalSceneChars > 0;

                                return (
                                  <Collapse.Panel
                                      header={
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 24 }}>
                                        <Space>
                                          <Tag color="blue">#{scene.orderIndex}</Tag>
                                          <Text strong>{scene.title}</Text>
                                          <Tag color={isDone ? 'success' : 'warning'}>
                                            {doneCount}/{totalPanels} panels done
                                          </Tag>
                                          {totalSceneChars > 0 && (
                                            <Tag 
                                              color={isOutfitsDone ? 'success' : 'orange'} 
                                              icon={isOutfitsDone ? <CheckCircleOutlined /> : <SkinOutlined />}
                                            >
                                              {isOutfitsDone ? 'All Outfits' : `${outfits.length}/${totalSceneChars} outfits`}
                                            </Tag>
                                          )}
                                          {scene.reason && (
                                            <Tag color="green" style={{ marginLeft: 8 }}>
                                              {scene.reason.substring(0, 50)}...
                                            </Tag>
                                          )}
                                        </Space>
                                        <Space onClick={e => e.stopPropagation()}>
                                          {totalPanels === 0 && (
                                            <Tooltip title="Tiến hành lên dàn ý Panels (Biên kịch) cho Scene này">
                                              <Button
                                                size="small"
                                                type="primary"
                                                icon={<RobotOutlined />}
                                                onClick={() => handleDraftPanels(scene)}
                                                loading={currentExtractingSceneId === scene.id}
                                              >
                                                Biên kịch Panels
                                              </Button>
                                            </Tooltip>
                                          )}
                                          {totalPanels > 0 && (
                                            <Tooltip title="Đạo diễn hình ảnh (AI) cho toàn bộ Scene">
                                              <Button
                                                size="small"
                                                type="primary"
                                                ghost
                                                icon={<VideoCameraOutlined />}
                                                onClick={() => handlePlanVisuals(scene)}
                                              >
                                                Đạo diễn hình ảnh (AI)
                                              </Button>
                                            </Tooltip>
                                          )}
                                          <Tooltip title="Quản lý trang phục (Outfit) cho các nhân vật trong scene">
                                            <Button
                                              size="small"
                                              icon={<SkinOutlined />}
                                              onClick={() => handleOpenSceneOutfits(scene)}
                                              style={(scene.outfits || []).length > 0 ? { color: '#fa8c16', borderColor: '#fa8c16' } : {}}
                                            >
                                              Trang phục ({(scene.outfits || []).length})
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={
                                            totalPanels === 0 
                                              ? "Vui lòng extract panels cho scene này trước" 
                                              : "Quản lý ảnh hàng loạt cho scene này"
                                          }>
                                            <Button
                                              size="small"
                                              type="dashed"
                                              icon={<RobotOutlined />}
                                              onClick={() => {
                                                setSelectedSceneForBulk(scene);
                                                setIsBulkModalVisible(true);
                                              }}
                                              disabled={totalPanels === 0}
                                              style={totalPanels > 0 
                                                ? (isDone ? { color: '#1890ff', borderColor: '#1890ff' } : { color: '#722ed1', borderColor: '#722ed1' }) 
                                                : {}
                                              }
                                            >
                                              Quản lý panel
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={scene.totalPanels === 0 ? "Vui lòng extract panels trước khi review" : "AI Review chất lượng nội dung các Panel hiện tại"}>
                                            <Button
                                              size="small"
                                              icon={<AuditOutlined />}
                                              onClick={() => handleReviewScene(scene)}
                                              disabled={!scene.totalPanels || scene.totalPanels === 0}
                                            >
                                              AI Review
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title="Yêu cầu AI tách lại toàn bộ Panel cho scene này dựa trên ý kiến của bạn">
                                            <Button
                                              size="small"
                                              icon={<ThunderboltOutlined />}
                                              onClick={() => handleReExtractPanels(scene)}
                                            >
                                              Re-extract
                                            </Button>
                                          </Tooltip>
                                        </Space>
                                      </div>
                                    }
                                    key={scene.id}
                                  >
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                      <Row gutter={[16, 16]}>
                                        <Col span={24}>
                                          {editingSceneId === scene.id ? (
                                            <SceneInlineEditor
                                              storyId={record.sourceStoryId}
                                              processId={record.id}
                                              scene={scene}
                                              onSuccess={(updatedScene) => {
                                                onSceneUpdated(record.id, updatedScene);
                                                setEditingSceneId(null);
                                              }}
                                              onCancel={() => setEditingSceneId(null)}
                                            />
                                          ) : (
                                            <Card 
                                              size="small" 
                                              title="Scene Overview" 
                                              headStyle={{ background: '#f0f2f5' }}
                                              extra={
                                                <Button 
                                                  type="text" 
                                                  size="small" 
                                                  icon={<EditOutlined />} 
                                                  onClick={() => setEditingSceneId(scene.id)}
                                                >
                                                  Edit
                                                </Button>
                                              }
                                            >
                                              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                                {scene.summary && (
                                                  <div>
                                                    <Text strong>Summary: </Text>
                                                    <Text>{scene.summary}</Text>
                                                  </div>
                                                )}
                                                {scene.setting && (
                                                  <div>
                                                    <Text strong>Setting: </Text>
                                                    <Text>{scene.setting}</Text>
                                                  </div>
                                                )}
                                                {scene.reason && (
                                                  <div>
                                                    <Text strong>Why it matters: </Text>
                                                    <Text italic style={{ color: '#8c8c8c' }}>{scene.reason}</Text>
                                                  </div>
                                                )}
                                                {scene.characterIds && (scene.characterIds as string[]).length > 0 && (
                                                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                    <Text strong>Characters: </Text>
                                                    {(scene.characterIds as string[]).map(id => (
                                                      <Tag key={id} style={{ margin: 0 }} color="blue">{getCharacterName(id)}</Tag>
                                                    ))}
                                                  </div>
                                                )}
                                              </Space>
                                            </Card>
                                          )}
                                        </Col>
                                        {(scene.keyMoments?.length || scene.endState) && (
                                          <Col span={24}>
                                            <Collapse 
                                              ghost 
                                              activeKey={activeDetailKeys[scene.id]}
                                              onChange={(key) => setActiveDetailKeys(prev => ({ ...prev, [scene.id]: key }))}
                                            >
                                              <Collapse.Panel header={<Text strong>Key Moments & End State</Text>} key="details">
                                                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                                  {scene.keyMoments && scene.keyMoments.length > 0 && (
                                                    <div>
                                                      <Text strong>Key Moments:</Text>
                                                      <div style={{ marginTop: 4 }}>
                                                        {scene.keyMoments.map((moment, i) => (
                                                          <Tag key={i} color="cyan" style={{ marginBottom: 4 }}>{moment}</Tag>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {scene.endState && (
                                                    <div>
                                                      <Text strong>End State:</Text>
                                                      <div style={{
                                                        marginTop: 4,
                                                        padding: '8px 12px',
                                                        background: '#f6ffed',
                                                        borderRadius: '4px',
                                                        borderLeft: '3px solid #52c41a'
                                                      }}>
                                                        {scene.endState}
                                                      </div>
                                                    </div>
                                                  )}
                                                </Space>
                                              </Collapse.Panel>
                                            </Collapse>
                                          </Col>
                                        )}
                                      </Row>
                                      
                                      <Divider style={{ margin: '8px 0' }} />
                                      
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={5} style={{ margin: 0 }}>
                                          <PictureOutlined style={{ marginRight: 8 }} />
                                          Panels Retelling
                                        </Title>
                                        <Space>
                                          {panels.length > 0 && (
                                            <Button
                                              danger
                                              size="small"
                                              icon={<DeleteOutlined />}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearPanels(scene);
                                              }}
                                            >
                                              Clear Panels
                                            </Button>
                                          )}
                                        </Space>
                                      </div>

                                      {panels.length === 0 && pLoading ? (
                                        <div style={{ textAlign: 'center', padding: '16px' }}>
                                          <Spin size="small" tip="Loading panels..." />
                                        </div>
                                      ) : (
                                        <Spin spinning={pLoading}>
                                          {panels.length === 0 ? (
                                            <Empty description="No panels extracted yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                          ) : (
                                            <List
                                              dataSource={panels}
                                              renderItem={(panel) => (
                                                <List.Item>
                                                  <Card size="small" style={{ width: '100%', borderLeft: '4px solid #1890ff' }}>
                                                    <Row gutter={16} align="top" wrap={false}>
                                                      <Col flex="auto">
                                                        <Space direction="vertical" style={{ width: '100%' }}>
                                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Space>
                                                              <Tag color="blue">Panel #{panel.sceneOrderIndex}</Tag>
                                                              {panel.mediaType === 'VIDEO' ? (
                                                                <Tag color="purple" icon={<VideoCameraOutlined />}>VIDEO</Tag>
                                                              ) : (
                                                                <Tag color="cyan" icon={<PictureOutlined />}>IMAGE</Tag>
                                                              )}
                                                              <Tag color="default">{countWords(panel.localizedCaption || panel.caption)} từ</Tag>
                                                              {panel.estimatedAudioDuration && (
                                                                <Tag color="orange">~{panel.estimatedAudioDuration}s audio</Tag>
                                                              )}
                                                              <Text italic style={{ color: '#555' }}> Storyteller retelling:</Text>
                                                            </Space>
                                                            <Space>
                                                              <Button 
                                                                size="small" 
                                                                icon={<PictureOutlined />}
                                                                onClick={() => handleManagePanelImage(panel)}
                                                              >
                                                                {panel.imagePath ? 'Manage' : 'Generate'}
                                                              </Button>
                                                              <Upload
                                                                accept="image/*"
                                                                showUploadList={false}
                                                                beforeUpload={(file) => {
                                                                  handleUploadPanelImage(panel, file);
                                                                  return false;
                                                                }}
                                                              >
                                                                <Button 
                                                                  size="small" 
                                                                  icon={<UploadOutlined />} 
                                                                >
                                                                  Upload
                                                                </Button>
                                                              </Upload>
                                                              <Tooltip title="Hiệu chỉnh nội dung bằng AI">
                                                                <Button
                                                                  size="small"
                                                                  icon={<ThunderboltOutlined />}
                                                                  onClick={() => handleAiRefinePanel(panel)}
                                                                >
                                                                  AI Refine
                                                                </Button>
                                                              </Tooltip>
                                                              <Popconfirm
                                                                title="Xóa Panel?"
                                                                onConfirm={() => handleDeletePanel(panel)}
                                                                okText="Xóa"
                                                                cancelText="Hủy"
                                                              >
                                                                <Button 
                                                                  size="small" 
                                                                  danger 
                                                                  icon={<DeleteOutlined />} 
                                                                />
                                                              </Popconfirm>
                                                            </Space>
                                                          </div>
                                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <div style={{ flex: 1 }}>
                                                              {editingPanelId === panel.id ? (
                                                                <PanelCaptionEditor
                                                                  storyId={record.sourceStoryId}
                                                                  panel={panel}
                                                                  onSuccess={(updatedPanel) => {
                                                                    onPanelUpdated(scene.id, updatedPanel);
                                                                    setEditingPanelId(null);
                                                                  }}
                                                                  onCancel={() => setEditingPanelId(null)}
                                                                />
                                                              ) : (
                                                                <div style={{ position: 'relative', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                                                                  {panel.localizedCaption || panel.caption}
                                                                  <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined style={{ fontSize: '12px' }} />}
                                                                    style={{ position: 'absolute', top: 4, right: 4 }}
                                                                    onClick={() => setEditingPanelId(panel.id)}
                                                                  />
                                                                </div>
                                                              )}
                                                            </div>
                                                          </div>
                                                          <Row gutter={8}>
                                                            <Col span={12}>
                                                              <Text strong style={{ fontSize: '12px' }}>Visual Description:</Text>
                                                              <div style={{ fontSize: '12px', color: '#666' }}>{panel.visualDescription}</div>
                                                            </Col>
                                                            <Col span={12}>
                                                              <Text strong style={{ fontSize: '12px' }}>Camera Setup:</Text>
                              <div style={{ fontSize: '12px', color: '#666' }}>{(panel as any).cameraSetup}</div>
                            </Col>
                          </Row>
                          {panel.characterIds && panel.characterIds.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: '12px' }}>Characters: </Text>
                              {panel.characterIds.map(id => (
                                <Tag key={id} style={{ fontSize: '10px' }}>{getCharacterName(id)}</Tag>
                              ))}
                            </div>
                          )}
                                                        </Space>
                                                      </Col>
                                                      {panel.imagePath && (
                                                        <Col flex="120px">
                                                            <div style={{ width: '120px', aspectRatio: props.processesData?.data.find(p => p.id === panel.processId)?.videoForm === 'SHORT' ? '9/16' : '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                                                            <img 
                                                              src={panel.imagePath} 
                                                              alt="Panel" 
                                                              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} 
                                                              onClick={() => handleManagePanelImage(panel)}
                                                            />
                                                          </div>
                                                        </Col>
                                                      )}
                                                    </Row>
                                                  </Card>
                                                </List.Item>
                                              )}
                                            />
                                          )}
                                        </Spin>
                                      )}
                                    </Space>
                                  </Collapse.Panel>
                                );
                              })}
                            </Collapse>
                          </>
                        )}
                      </Spin>
                    )}
                  </div>
                )}
              </div>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            if (record.isGroup) {
              return expanded ? (
                <DownOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                  onClick={e => onExpand(record, e)}
                />
              ) : (
                <RightOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                  onClick={e => onExpand(record, e)}
                />
              );
            }
            return expanded ? (
              <DownOutlined
                rotate={180}
                style={{ marginRight: 8, color: '#8c8c8c' }}
                onClick={e => onExpand(record, e)}
              />
            ) : (
              <DownOutlined 
                style={{ marginRight: 8, color: '#8c8c8c' }} 
                onClick={e => onExpand(record, e)} 
              />
            );
          },
        }}
      />
      ) : (
        <div style={{ marginTop: 8 }}>
          {processesLoading && !processesData ? (
            <Card loading />
          ) : (
            (processesData?.data || []).map(process => (
              <ProcessCard key={process.id} process={process} />
            ))
          )}
        </div>
      )}
    </Card>
  );
};

export default ProcessPanelRows;
